/**
 * Subprocessors — InfoSec gate
 * =============================
 * Industry-standard vendor disclosure for enterprise due diligence,
 * gated behind a quick email confirmation so that the list reaches
 * real reviewers (who happily fill the form) and not casual visitors
 * or competitors looking for our stack.
 *
 * On submit the email is captured into `lead_magnet_subscribers` with
 * magnet_slug=`subprocessor_list` — surfaces in /admin/sales-signals
 * and fires a high-intent Slack ping.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '../components/ui/card';
import {
  Shield, Lock, ArrowLeft, Mail, Loader2, CheckCircle2, FileCheck2,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subprocessors = () => {
  const [submitting, setSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [list, setList] = useState([]);
  const [lastUpdated, setLastUpdated] = useState('');
  const [notice, setNotice] = useState('');

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) {
      toast.error('A work email helps us notify you of changes');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/subprocessors/request`, {
        email: email.trim(),
        name: name.trim() || undefined,
        company: company.trim() || undefined,
        role: role.trim() || undefined,
      });
      setList(data.subprocessors || []);
      setLastUpdated(data.last_updated || '');
      setNotice(data.notice || '');
      setUnlocked(true);
      toast.success('List unlocked', {
        description: data.deduped
          ? "You're already on our notifications list — thank you."
          : "We'll email you 14+ days before adding any new vendor.",
      });
    } catch (err) {
      toast.error("We couldn't unlock the list just now", {
        description: err?.response?.data?.detail || err?.message || 'Mind trying again?',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="subprocessors-page">
      {/* Top nav */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 text-slate-100 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/privacy-policy">
              <Button variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Privacy Policy
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-start gap-3 mb-8">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center shrink-0">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"><BrandTitle>Subprocessor list</BrandTitle></h1>
              <p className="text-slate-400 text-sm mt-1">For procurement and InfoSec teams · GDPR Art. 28 / CCPA / SOC 2 disclosures</p>
            </div>
          </div>

          <p className="text-slate-600 leading-relaxed mb-8">
            We work with a small, vetted set of third-party vendors to operate CARTIEAI — for payment processing, transactional email, hosting, and AI-assisted features. To make sure the list reaches real reviewers (and not casual visitors), we share it after a quick email confirmation. We'll then notify you at least <strong>14 days before</strong> adding any new subprocessor that handles personal data, so you can keep your vendor file current with no extra work on your side.
          </p>

          {!unlocked ? (
            <Card className="bg-slate-900 text-slate-100 border-emerald-500/20" data-testid="subprocessor-gate">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Lock className="h-5 w-5 text-emerald-400" />
                  Confirm your details to view the list
                </CardTitle>
                <CardDescription className="text-slate-400">
                  We use your email only to notify you of subprocessor changes — never for marketing. You can reply "stop" anytime to be removed.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">Work email *</label>
                    <Input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@yourcompany.com"
                      autoFocus
                      data-testid="subp-email-input"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">Your name</label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Jane Doe"
                        data-testid="subp-name-input"
                        className="bg-slate-950 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">Company</label>
                      <Input
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder="Acme Corp"
                        data-testid="subp-company-input"
                        className="bg-slate-950 border-slate-700 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">Role</label>
                      <Input
                        value={role}
                        onChange={(e) => setRole(e.target.value)}
                        placeholder="InfoSec / Procurement"
                        data-testid="subp-role-input"
                        className="bg-slate-950 border-slate-700 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
                    <p className="text-xs text-slate-400">By submitting, you agree to our <Link to="/privacy-policy" className="text-emerald-400">Privacy Policy</Link>.</p>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-emerald-500 hover:bg-emerald-400 text-white"
                      data-testid="subp-submit-btn"
                    >
                      {submitting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Unlocking…</>
                      ) : (
                        <><Mail className="h-4 w-4 mr-2" />Show me the list</>
                      )}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6" data-testid="subprocessor-list-revealed">
              <div className="flex items-center gap-2 p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                <p className="text-sm text-emerald-200">
                  <strong>You're on the notifications list.</strong> {notice}
                </p>
              </div>

              <Card className="bg-slate-900 text-slate-100 border-white/10">
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="flex items-center gap-2 text-white">
                      <FileCheck2 className="h-5 w-5 text-emerald-400" />
                      Current subprocessors
                    </CardTitle>
                    {lastUpdated && (
                      <span className="text-xs text-slate-400">Last updated {lastUpdated}</span>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-slate-400 border-b border-white/10">
                          <th className="font-semibold p-3">Vendor</th>
                          <th className="font-semibold p-3">Purpose</th>
                          <th className="font-semibold p-3">Data shared</th>
                          <th className="font-semibold p-3">Location</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {list.map((s, i) => (
                          <tr key={i} className="text-slate-400" data-testid={`subp-row-${i}`}>
                            <td className="p-3 text-white font-medium align-top">{s.vendor}</td>
                            <td className="p-3 align-top">{s.purpose}</td>
                            <td className="p-3 align-top">{s.data_shared}</td>
                            <td className="p-3 align-top text-slate-400">{s.location}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <p className="text-xs text-slate-400 italic">
                Need a Data Processing Agreement (DPA) or extended security questionnaire? Email <a href="mailto:hello@cartieai.com" className="text-emerald-400">hello@cartieai.com</a> with the subject "DPA request" — typical turnaround is one business day.
              </p>
            </div>
          )}
        </div>
      </main>

      <footer className="py-6 bg-slate-950 border-t border-white/5 text-center text-xs text-slate-400" data-testid="subprocessors-footer">
        © 2026 CARTIEAI LLC · Texas, USA · <Link to="/privacy-policy" className="hover:text-slate-600">Privacy</Link> · <Link to="/terms" className="hover:text-slate-600">Terms</Link>
      </footer>
    </div>
  );
};

export default Subprocessors;
